/**
 * GeoIpDatabaseCard (OCP-848) - which GeoIP country database does the api
 * serve, and how old is it?
 *
 * Since 2026-10-13 account.orboto.io and mail.orboto.io are DNS-only, so the
 * country of every portal login (new-country takeover alert), redeem attempt
 * and redeem region pick on those hosts comes from a local DB-IP database
 * instead of Cloudflare's CF-IPCountry. A daily job downloads the monthly
 * release; this card shows the served release, the last refresh attempt and
 * the open staleness alert, and "Refresh now" downloads the newest release
 * (same audited route as the `ocp_geoip_status` MCP tool).
 *
 * Self-fetching so the page's other panels fail independently.
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

interface GeoIpView {
  loaded: boolean;
  source: 'bundled' | 'refreshed' | null;
  database_type: string | null;
  build_date: string | null;
  age_days: number | null;
  max_age_days: number;
  stale: boolean;
  path: string | null;
  attribution: { text: string; url: string; license: string; license_url: string };
  refresh: {
    last_at: string | null;
    last_outcome: string | null;
    last_month: string | null;
    last_error: string | null;
    last_success_at: string | null;
  };
  alert: { alerting: boolean; since: string | null; last_alerted_at: string | null };
}

interface RefreshResponse {
  refresh: { outcome: string; month: string | null; error: string | null };
}

const SOURCE_LABEL: Record<'bundled' | 'refreshed', string> = {
  bundled: 'image copy',
  refreshed: 'downloaded by the refresh job',
};

const OUTCOME_LABEL: Record<string, string> = {
  updated: 'Downloaded a newer release',
  up_to_date: 'Already the newest release',
  not_published: 'No release downloadable yet',
  failed: 'Failed',
};

export function GeoIpDatabaseCard() {
  const [data, setData] = useState<GeoIpView | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api<GeoIpView>('/v1/admin/geoip');
      // An older api (before OCP-848) or a proxy error page: no crash, a message.
      if (!res || typeof res.loaded !== 'boolean' || !res.refresh || !res.alert || !res.attribution) {
        setData(null);
        setLoadError('The api did not return a GeoIP database state.');
        return;
      }
      setData(res);
    } catch (err) {
      setLoadError(
        err instanceof ApiError
          ? `Could not load the GeoIP database state (HTTP ${err.status}).`
          : 'Could not load the GeoIP database state.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshNow = useCallback(async () => {
    setRefreshing(true);
    setNote(null);
    try {
      const res = await api<RefreshResponse>('/v1/admin/geoip/refresh', { method: 'POST', body: {} });
      const label = OUTCOME_LABEL[res.refresh.outcome] ?? res.refresh.outcome;
      setNote(res.refresh.error ? `${label}: ${res.refresh.error}` : `${label}${res.refresh.month ? ` (${res.refresh.month})` : ''}.`);
      await load();
    } catch (err) {
      setNote(err instanceof ApiError ? `Refresh failed (HTTP ${err.status}).` : 'Refresh failed.');
    } finally {
      setRefreshing(false);
    }
  }, [load]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section
      data-testid="geoip-database-card"
      className="mt-6 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">GeoIP country database</h2>
          <p className="mt-1 max-w-2xl text-xs text-gray-500 dark:text-gray-400">
            account.orboto.io and mail.orboto.io are DNS-only, so login geo, the new-country alert and the
            redeem region resolve the country from this local database instead of Cloudflare. A daily job
            downloads the monthly release; you are alerted when it is older than{' '}
            {data?.max_age_days ?? 45} days.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void refreshNow()}
            disabled={refreshing || loading}
            data-testid="geoip-refresh-now"
            className="rounded border border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 px-3 py-1.5 text-xs font-semibold text-blue-800 dark:text-blue-200 hover:bg-blue-100 disabled:opacity-50"
          >
            {refreshing ? 'Downloading…' : 'Refresh now'}
          </button>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            {loading ? 'Reloading…' : 'Reload'}
          </button>
        </div>
      </div>

      {loadError && (
        <div
          role="alert"
          className="mt-4 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
        >
          {loadError}
        </div>
      )}

      {note && (
        <p data-testid="geoip-refresh-note" className="mt-3 text-sm text-gray-700 dark:text-gray-300">
          {note}
        </p>
      )}

      {!loadError && data && (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span
              data-testid="geoip-status"
              className={`inline-flex items-center rounded px-2 py-1 text-xs font-semibold uppercase tracking-wide ring-1 ring-inset ${
                data.stale
                  ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 ring-red-200 dark:ring-red-800'
                  : 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 ring-green-200 dark:ring-green-800'
              }`}
            >
              {!data.loaded ? 'No database loaded' : data.stale ? 'Outdated' : 'Current'}
            </span>
            {data.loaded && data.build_date && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                release of {data.build_date.slice(0, 10)} · {data.age_days} days old
                {data.source ? ` · ${SOURCE_LABEL[data.source]}` : ''}
              </span>
            )}
            {data.path && <span className="font-mono text-xs text-gray-400 break-all">{data.path}</span>}
          </div>

          {!data.loaded && (
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
              Every login and redeem on the DNS-only hosts records country null until a database is loaded.
            </p>
          )}

          {data.alert.alerting && (
            <div
              role="alert"
              className="mt-4 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
            >
              <span className="font-semibold">Alert open</span>
              {data.alert.since ? ` since ${fmtDateTime(data.alert.since)}` : ''} - the served database is older
              than {data.max_age_days} days or missing. Refresh now, or check the last refresh error below.
            </div>
          )}

          <dl className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
            <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-3">
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Last refresh</dt>
              <dd className="mt-1 text-gray-900 dark:text-gray-100" data-testid="geoip-last-refresh">
                {data.refresh.last_at
                  ? `${OUTCOME_LABEL[data.refresh.last_outcome ?? ''] ?? data.refresh.last_outcome} · ${fmtDateTime(data.refresh.last_at)}`
                  : 'Not run yet'}
              </dd>
              {data.refresh.last_error && (
                <dd className="mt-1 text-xs text-red-700 dark:text-red-300 break-words">{data.refresh.last_error}</dd>
              )}
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-3">
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Last successful check</dt>
              <dd className="mt-1 text-gray-900 dark:text-gray-100">
                {data.refresh.last_success_at ? fmtDateTime(data.refresh.last_success_at) : '-'}
              </dd>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-3">
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Source</dt>
              <dd className="mt-1 text-gray-900 dark:text-gray-100">
                {data.database_type ?? 'DB-IP IP to Country Lite'}, {data.attribution.license}
              </dd>
            </div>
          </dl>

          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            <a href={data.attribution.url} target="_blank" rel="noreferrer" className="underline">
              {data.attribution.text}
            </a>{' '}
            (
            <a href={data.attribution.license_url} target="_blank" rel="noreferrer" className="underline">
              {data.attribution.license}
            </a>
            )
          </p>
        </>
      )}
    </section>
  );
}
